import React, { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Sidebar
    nav: {
      overview: 'Overview',
      residents: 'Residents',
      operations: 'Operations',
      analytics: 'Analytics',
      executiveDashboard: 'Executive Dashboard',
      residentDatabase: 'Resident Database',
      reservationRequests: 'Reservation Requests',
      occupancyAnalytics: 'Occupancy Analytics',
      buildingPerformance: 'Building Performance',
      checkinCheckout: 'Check-in / Check-out',
      roomManagement: 'Room Management',
      calendarView: 'Calendar View',
      residentLifecycle: 'Resident Lifecycle',
      advancedFiltering: 'Advanced Filtering',
      advancedReporting: 'Advanced Reporting',
      roomTransfer: 'Room Transfer',
      emailTemplateEditor: 'Email Templates',
      lightMode: 'Light Mode',
      darkMode: 'Dark Mode',
      signOut: 'Sign Out',
    },
    // Header
    header: {
      appName: 'US Réservation Résidences',
      appShort: 'USRR',
    },
    // Login
    login: {
      welcomeBack: 'Welcome back',
      signInToContinue: 'Sign in to your account to continue',
      createAccount: 'Create account',
      registerToAccess: 'Register to access the platform',
      signIn: 'Sign In',
      register: 'Register',
      emailAddress: 'Email Address',
      password: 'Password',
      fullName: 'Full Name',
      role: 'Role',
      confirmPassword: 'Confirm Password',
      signingIn: 'Signing in...',
      creatingAccount: 'Creating account...',
      demoAccounts: 'Demo Accounts',
      premiumResidence: 'Premium Residence',
      management: 'Management',
      streamlineOps: 'Streamline operations across all buildings — from reservations to real-time occupancy tracking.',
      totalRooms: 'Total Rooms',
      buildings: 'Buildings',
      occupancy: 'Occupancy',
      residents: 'Residents',
      guestRole: 'Guest — Submit reservation requests',
      viewerRole: 'Viewer — Read-only access',
      adminRole: 'Admin — Full access',
      minPassword: 'Min. 6 characters',
      repeatPassword: 'Repeat password',
      yourFullName: 'Your full name',
      networkError: 'Network error. Please try again.',
      passwordMismatch: 'Passwords do not match.',
      passwordTooShort: 'Password must be at least 6 characters.',
      forgotPassword: 'Forgot password?',
      resetPassword: 'Reset Password',
      resetPasswordDesc: 'Enter your email address and we\'ll send you a link to reset your password.',
      sendResetLink: 'Send Reset Link',
      sendingResetLink: 'Sending...',
      resetEmailSent: 'Password reset email sent! Check your inbox.',
      backToSignIn: 'Back to Sign In',
      newPasswordDesc: 'Enter your new password below.',
      newPassword: 'New Password',
      updatePassword: 'Update Password',
      updating: 'Updating…',
      passwordUpdated: 'Password updated successfully!',
      redirectingToLogin: 'Redirecting you to the login page…',
      verifyingLink: 'Verifying reset link…',
    },
    // Executive Dashboard
    dashboard: {
      title: 'Executive Dashboard',
      overallOccupancy: 'Overall Occupancy Rate',
      revenuePerformance: 'Revenue Performance',
      residentSatisfaction: 'Resident Satisfaction',
      capacityUtilization: 'Capacity Utilization',
      quickActions: 'Quick Actions',
      newReservation: 'New Reservation',
      checkIn: 'Check In',
      generateReport: 'Generate Report',
      yearComparison: 'Year Comparison',
      allBuildings: 'All Buildings',
      exportData: 'Export Data',
    },
    // Room Management
    rooms: {
      title: 'Room Management',
      addRoom: 'Add Room',
      editRoom: 'Edit Room',
      deleteRoom: 'Delete Room',
      roomNumber: 'Room Number',
      building: 'Building',
      floor: 'Floor',
      type: 'Type',
      capacity: 'Capacity',
      status: 'Status',
      notes: 'Notes',
      save: 'Save',
      cancel: 'Cancel',
      search: 'Search rooms...',
      allBuildings: 'All Buildings',
      allStatuses: 'All Statuses',
      allTypes: 'All Types',
      available: 'Available',
      occupied: 'Occupied',
      maintenance: 'Maintenance',
      reserved: 'Reserved',
      groundFloor: 'Ground Floor',
      totalRooms: 'Total Rooms',
      filters: 'Filters',
      actions: 'Actions',
    },
    // Occupancy Analytics
    occupancy: {
      title: 'Occupancy Analytics',
      currentOccupancyRate: 'Current Occupancy Rate',
      availableRooms: 'Available Rooms',
      pendingCheckins: 'Pending Check-ins',
      capacityAlerts: 'Capacity Alerts',
      acrossAllBuildings: 'Across all buildings',
      expectedToday: 'Expected today',
      buildingsAbove90: 'Buildings above 90%',
      onSchedule: 'On schedule',
      filters: 'Filters',
      refresh: 'Refresh',
      activityFeed: 'Activity Feed',
      heatmap: 'Occupancy Heatmap',
      trendChart: 'Occupancy Trend',
      viewDetails: 'View Details',
      viewSchedule: 'View Schedule',
      dismiss: 'Dismiss',
    },
    // Resident Database
    residentDb: {
      title: 'Resident Database',
      addResident: 'Add Resident',
      search: 'Search residents...',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      room: 'Room',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      status: 'Status',
      actions: 'Actions',
      active: 'Active',
      inactive: 'Inactive',
      export: 'Export',
      filters: 'Filters',
    },
    // Reservation Requests
    reservations: {
      title: 'Reservation Requests',
      newRequest: 'New Request',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      approve: 'Approve',
      reject: 'Reject',
      viewDetails: 'View Details',
      requestDate: 'Request Date',
      requestedBy: 'Requested By',
      roomType: 'Room Type',
      duration: 'Duration',
      status: 'Status',
    },
    // Check-in / Check-out
    checkin: {
      title: 'Check-in / Check-out',
      checkIn: 'Check In',
      checkOut: 'Check Out',
      residentName: 'Resident Name',
      roomNumber: 'Room Number',
      date: 'Date',
      confirm: 'Confirm',
      cancel: 'Cancel',
      search: 'Search...',
    },
    // Calendar
    calendar: {
      title: 'Calendar View',
      today: 'Today',
      month: 'Month',
      week: 'Week',
      day: 'Day',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      maintenance: 'Maintenance',
      reservation: 'Reservation',
    },
    // Building Performance
    buildingPerf: {
      title: 'Building Performance',
      rank: 'Rank',
      building: 'Building',
      occupancyRate: 'Occupancy Rate',
      satisfaction: 'Satisfaction',
      totalRooms: 'Total Rooms',
      performance: 'Performance',
      alerts: 'Alerts',
      compare: 'Compare',
    },
    // Resident Lifecycle
    lifecycle: {
      title: 'Resident Lifecycle',
      cohort: 'Cohort',
      stage: 'Stage',
      retention: 'Retention',
      demographics: 'Demographics',
      journey: 'Journey',
    },
    // Advanced Filtering
    filtering: {
      title: 'Advanced Filtering',
      addFilter: 'Add Filter',
      savedQueries: 'Saved Queries',
      export: 'Export',
      results: 'Results',
      apply: 'Apply',
      reset: 'Reset',
      save: 'Save Query',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      close: 'Close',
      edit: 'Edit',
      delete: 'Delete',
      confirm: 'Confirm',
      cancel: 'Cancel',
      save: 'Save',
      search: 'Search',
      filter: 'Filter',
      export: 'Export',
      refresh: 'Refresh',
      noData: 'No data available',
      viewAll: 'View All',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      of: 'of',
      page: 'Page',
      rows: 'rows',
      total: 'Total',
      from: 'From',
      to: 'To',
      date: 'Date',
      name: 'Name',
      status: 'Status',
      actions: 'Actions',
      yes: 'Yes',
      no: 'No',
    },
  },
  fr: {
    // Sidebar
    nav: {
      overview: 'Vue d\'ensemble',
      residents: 'Résidents',
      operations: 'Opérations',
      analytics: 'Analytiques',
      executiveDashboard: 'Tableau de bord',
      residentDatabase: 'Base de données résidents',
      reservationRequests: 'Demandes de réservation',
      occupancyAnalytics: 'Analyse d\'occupation',
      buildingPerformance: 'Performance des bâtiments',
      checkinCheckout: 'Entrée / Sortie',
      roomManagement: 'Gestion des chambres',
      calendarView: 'Vue calendrier',
      residentLifecycle: 'Cycle de vie résident',
      advancedFiltering: 'Filtrage avancé',
      advancedReporting: 'Rapports avancés',
      roomTransfer: 'Transfert de chambre',
      emailTemplateEditor: 'Modèles d\'e-mail',
      lightMode: 'Mode clair',
      darkMode: 'Mode sombre',
      signOut: 'Déconnexion',
    },
    // Header
    header: {
      appName: 'US Réservation Résidences',
      appShort: 'USRR',
    },
    // Login
    login: {
      welcomeBack: 'Bienvenue',
      signInToContinue: 'Connectez-vous à votre compte pour continuer',
      createAccount: 'Créer un compte',
      registerToAccess: 'Inscrivez-vous pour accéder à la plateforme',
      signIn: 'Connexion',
      register: 'S\'inscrire',
      emailAddress: 'Adresse e-mail',
      password: 'Mot de passe',
      fullName: 'Nom complet',
      role: 'Rôle',
      confirmPassword: 'Confirmer le mot de passe',
      signingIn: 'Connexion en cours...',
      creatingAccount: 'Création du compte...',
      demoAccounts: 'Comptes de démonstration',
      premiumResidence: 'Gestion Premium',
      management: 'des Résidences',
      streamlineOps: 'Optimisez les opérations dans tous les bâtiments — des réservations au suivi d\'occupation en temps réel.',
      totalRooms: 'Chambres totales',
      buildings: 'Bâtiments',
      occupancy: 'Occupation',
      residents: 'Résidents',
      guestRole: 'Invité — Soumettre des demandes de réservation',
      viewerRole: 'Observateur — Accès en lecture seule',
      adminRole: 'Administrateur — Accès complet',
      minPassword: 'Min. 6 caractères',
      repeatPassword: 'Répéter le mot de passe',
      yourFullName: 'Votre nom complet',
      networkError: 'Erreur réseau. Veuillez réessayer.',
      passwordMismatch: 'Les mots de passe ne correspondent pas.',
      passwordTooShort: 'Le mot de passe doit comporter au moins 6 caractères.',
      forgotPassword: 'Mot de passe oublié ?',
      resetPassword: 'Réinitialiser le mot de passe',
      resetPasswordDesc: 'Entrez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.',
      sendResetLink: 'Envoyer le lien',
      sendingResetLink: 'Envoi en cours...',
      resetEmailSent: 'E-mail de réinitialisation envoyé ! Vérifiez votre boîte de réception.',
      backToSignIn: 'Retour à la connexion',
      newPasswordDesc: 'Entrez votre nouveau mot de passe ci-dessous.',
      newPassword: 'Nouveau mot de passe',
      updatePassword: 'Mettre à jour le mot de passe',
      updating: 'Mise à jour…',
      passwordUpdated: 'Mot de passe mis à jour avec succès !',
      redirectingToLogin: 'Redirection vers la page de connexion…',
      verifyingLink: 'Vérification du lien…',
    },
    // Executive Dashboard
    dashboard: {
      title: 'Tableau de bord',
      overallOccupancy: 'Taux d\'occupation global',
      revenuePerformance: 'Performance des revenus',
      residentSatisfaction: 'Satisfaction des résidents',
      capacityUtilization: 'Utilisation de la capacité',
      quickActions: 'Actions rapides',
      newReservation: 'Nouvelle réservation',
      checkIn: 'Enregistrement',
      generateReport: 'Générer un rapport',
      yearComparison: 'Comparaison annuelle',
      allBuildings: 'Tous les bâtiments',
      exportData: 'Exporter les données',
    },
    // Room Management
    rooms: {
      title: 'Gestion des chambres',
      addRoom: 'Ajouter une chambre',
      editRoom: 'Modifier la chambre',
      deleteRoom: 'Supprimer la chambre',
      roomNumber: 'Numéro de chambre',
      building: 'Bâtiment',
      floor: 'Étage',
      type: 'Type',
      capacity: 'Capacité',
      status: 'Statut',
      notes: 'Notes',
      save: 'Enregistrer',
      cancel: 'Annuler',
      search: 'Rechercher des chambres...',
      allBuildings: 'Tous les bâtiments',
      allStatuses: 'Tous les statuts',
      allTypes: 'Tous les types',
      available: 'Disponible',
      occupied: 'Occupée',
      maintenance: 'Maintenance',
      reserved: 'Réservée',
      groundFloor: 'Rez-de-chaussée',
      totalRooms: 'Total chambres',
      filters: 'Filtres',
      actions: 'Actions',
    },
    // Occupancy Analytics
    occupancy: {
      title: 'Analyse d\'occupation',
      currentOccupancyRate: 'Taux d\'occupation actuel',
      availableRooms: 'Chambres disponibles',
      pendingCheckins: 'Enregistrements en attente',
      capacityAlerts: 'Alertes de capacité',
      acrossAllBuildings: 'Dans tous les bâtiments',
      expectedToday: 'Attendus aujourd\'hui',
      buildingsAbove90: 'Bâtiments au-dessus de 90%',
      onSchedule: 'Dans les délais',
      filters: 'Filtres',
      refresh: 'Actualiser',
      activityFeed: 'Flux d\'activité',
      heatmap: 'Carte thermique d\'occupation',
      trendChart: 'Tendance d\'occupation',
      viewDetails: 'Voir les détails',
      viewSchedule: 'Voir le planning',
      dismiss: 'Ignorer',
    },
    // Resident Database
    residentDb: {
      title: 'Base de données résidents',
      addResident: 'Ajouter un résident',
      search: 'Rechercher des résidents...',
      name: 'Nom',
      email: 'E-mail',
      phone: 'Téléphone',
      room: 'Chambre',
      checkIn: 'Arrivée',
      checkOut: 'Départ',
      status: 'Statut',
      actions: 'Actions',
      active: 'Actif',
      inactive: 'Inactif',
      export: 'Exporter',
      filters: 'Filtres',
    },
    // Reservation Requests
    reservations: {
      title: 'Demandes de réservation',
      newRequest: 'Nouvelle demande',
      pending: 'En attente',
      approved: 'Approuvée',
      rejected: 'Rejetée',
      approve: 'Approuver',
      reject: 'Rejeter',
      viewDetails: 'Voir les détails',
      requestDate: 'Date de demande',
      requestedBy: 'Demandé par',
      roomType: 'Type de chambre',
      duration: 'Durée',
      status: 'Statut',
    },
    // Check-in / Check-out
    checkin: {
      title: 'Entrée / Sortie',
      checkIn: 'Enregistrement',
      checkOut: 'Départ',
      residentName: 'Nom du résident',
      roomNumber: 'Numéro de chambre',
      date: 'Date',
      confirm: 'Confirmer',
      cancel: 'Annuler',
      search: 'Rechercher...',
    },
    // Calendar
    calendar: {
      title: 'Vue calendrier',
      today: 'Aujourd\'hui',
      month: 'Mois',
      week: 'Semaine',
      day: 'Jour',
      checkIn: 'Arrivée',
      checkOut: 'Départ',
      maintenance: 'Maintenance',
      reservation: 'Réservation',
    },
    // Building Performance
    buildingPerf: {
      title: 'Performance des bâtiments',
      rank: 'Rang',
      building: 'Bâtiment',
      occupancyRate: 'Taux d\'occupation',
      satisfaction: 'Satisfaction',
      totalRooms: 'Total chambres',
      performance: 'Performance',
      alerts: 'Alertes',
      compare: 'Comparer',
    },
    // Resident Lifecycle
    lifecycle: {
      title: 'Cycle de vie résident',
      cohort: 'Cohorte',
      stage: 'Étape',
      retention: 'Rétention',
      demographics: 'Démographie',
      journey: 'Parcours',
    },
    // Advanced Filtering
    filtering: {
      title: 'Filtrage avancé',
      addFilter: 'Ajouter un filtre',
      savedQueries: 'Requêtes sauvegardées',
      export: 'Exporter',
      results: 'Résultats',
      apply: 'Appliquer',
      reset: 'Réinitialiser',
      save: 'Sauvegarder la requête',
    },
    // Common
    common: {
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      close: 'Fermer',
      edit: 'Modifier',
      delete: 'Supprimer',
      confirm: 'Confirmer',
      cancel: 'Annuler',
      save: 'Enregistrer',
      search: 'Rechercher',
      filter: 'Filtrer',
      export: 'Exporter',
      refresh: 'Actualiser',
      noData: 'Aucune donnée disponible',
      viewAll: 'Voir tout',
      back: 'Retour',
      next: 'Suivant',
      previous: 'Précédent',
      of: 'sur',
      page: 'Page',
      rows: 'lignes',
      total: 'Total',
      from: 'De',
      to: 'À',
      date: 'Date',
      name: 'Nom',
      status: 'Statut',
      actions: 'Actions',
      yes: 'Oui',
      no: 'Non',
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    try { return localStorage.getItem('language') || 'en'; } catch { return 'en'; }
  });

  const toggleLanguage = () => {
    const next = language === 'en' ? 'fr' : 'en';
    setLanguage(next);
    try { localStorage.setItem('language', next); } catch {}
  };

  const t = translations?.[language] || translations?.en;

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

export default LanguageContext;
